<template>
  <div class="preset-form">
    <div v-for="field in schema" :key="field.key" class="form-group">
      <label class="form-label" :class="{ 'required-label': field.required }">{{ field.label }}</label>

      <!-- 圖片上傳 -->
      <ImageUploader
        v-if="field.type === 'image'"
        :modelValue="fields[field.key] || ''"
        @update:modelValue="updateField(field.key, $event)"
        :upload-fn="uploadFn"
        shape="card"
        accept="image/jpeg,image/png"
        hint="僅支援 JPEG / PNG（LINE 不接受 WebP）"
        :aspect-ratio="cropSize(field).css"
        :crop-width="cropSize(field).width"
        :crop-height="cropSize(field).height"
        :crop-only-if-aspect-mismatch="true"
        :size-hint="cropSize(field).width
          ? `上傳後可拖曳位置、放大縮小，輸出 ${cropSize(field).width} × ${cropSize(field).height} px`
          : ''"
        :placeholder="field.placeholder || '上傳圖片'"
      />

      <!-- 多行文字 -->
      <textarea
        v-else-if="field.type === 'textarea'"
        class="form-control"
        :value="fields[field.key] || ''"
        @input="updateField(field.key, $event.target.value)"
        :placeholder="field.placeholder || ''"
        :maxlength="field.maxLength || 500"
        rows="3"
      ></textarea>

      <!-- URL（舊版相容，保留顯示） -->
      <input
        v-else-if="field.type === 'url'"
        type="url"
        class="form-control"
        :value="fields[field.key] || ''"
        @input="updateField(field.key, $event.target.value)"
        :placeholder="field.placeholder || 'https://...'"
      />

      <!-- 下拉選單 -->
      <select
        v-else-if="field.type === 'select'"
        class="form-control"
        :value="fields[field.key] || field.default || ''"
        @change="updateField(field.key, $event.target.value)"
      >
        <option v-for="opt in field.options || []" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
      </select>

      <!-- 名片配色（文字＋按鈕成套） -->
      <div v-else-if="field.type === 'colorscheme'" class="scheme-picker">
        <button
          v-for="opt in (field.options || [])"
          :key="opt.value"
          type="button"
          class="scheme-swatch"
          :class="{ active: currentScheme(field) === opt.value }"
          :style="{ backgroundColor: opt.swatch }"
          :title="opt.label"
          :aria-label="opt.label"
          :aria-pressed="currentScheme(field) === opt.value"
          @click="updateField(field.key, opt.value)"
        >
          <i v-if="currentScheme(field) === opt.value" class="ri-check-line"></i>
        </button>
        <span class="scheme-label">{{ schemeLabel(field) }}</span>
      </div>

      <!-- 顏色 -->
      <div v-else-if="field.type === 'color'" class="color-field">
        <input
          type="color"
          class="color-swatch"
          :value="swatchColor(fields[field.key] || field.default)"
          @input="updateField(field.key, $event.target.value.toUpperCase())"
        />
        <input
          type="text"
          class="form-control"
          :value="fields[field.key] || ''"
          @input="updateField(field.key, $event.target.value)"
          :placeholder="field.default || '#FFFFFF'"
          maxlength="7"
        />
      </div>

      <!-- 核取方塊 -->
      <label v-else-if="field.type === 'checkbox'" class="checkbox-field">
        <input
          type="checkbox"
          :checked="isChecked(field)"
          @change="updateField(field.key, $event.target.checked)"
        />
        <span>顯示在名片上</span>
      </label>

      <!-- 動作類型選擇（網址 / 文字 / Postback / 分享好友 / 電話） -->
      <LineActionField
        v-else-if="field.type === 'action'"
        share-flex-message
        :modelValue="fields[field.key] || {}"
        @update:modelValue="updateField(field.key, $event)"
      />

      <!-- 按鈕清單（template_card 等多按鈕版型） -->
      <div v-else-if="field.type === 'buttons'" class="buttons-editor">
        <div
          v-for="(btn, idx) in (fields[field.key] || [{}])"
          :key="idx"
          class="button-row"
        >
          <div class="button-row-header">
            <span class="button-row-label">按鈕 {{ idx + 1 }}</span>
            <button type="button" class="icon-btn-sm danger" @click="removeButtonRow(field.key, idx)">
              <i class="ri-close-line"></i>
            </button>
          </div>
          <input
            type="text"
            class="form-control form-control-sm"
            :value="btn.label || ''"
            @input="updateButtonField(field.key, idx, 'label', $event.target.value)"
            placeholder="按鈕文字"
            style="margin-bottom:6px;"
          />
          <LineActionField
            share-flex-message
            :modelValue="btnActionValue(btn)"
            @update:modelValue="updateButtonAction(field.key, idx, $event)"
          />
        </div>
        <button
          v-if="(fields[field.key] || []).length < 4"
          type="button"
          class="btn-add-item"
          @click="addButtonRow(field.key)"
        >
          <i class="ri-add-line"></i> 新增按鈕（{{ (fields[field.key] || []).length }}/4）
        </button>
      </div>

      <!-- 項目清單（如 order_confirm 的 items） -->
      <div v-else-if="field.type === 'items'" class="items-editor">
        <div v-for="(item, idx) in (fields[field.key] || [{}])" :key="idx" class="item-row">
          <div v-for="sub in field.itemFields" :key="sub.key" class="item-field">
            <input
              type="text"
              class="form-control form-control-sm"
              :value="item[sub.key] || ''"
              @input="updateItemField(field.key, idx, sub.key, $event.target.value)"
              :placeholder="sub.label"
            />
          </div>
          <button type="button" class="icon-btn-sm danger" @click="removeItemRow(field.key, idx)">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <button type="button" class="btn-add-item" @click="addItemRow(field.key)">
          <i class="ri-add-line"></i> 新增項目
        </button>
      </div>

      <!-- 一般文字 -->
      <input
        v-else
        type="text"
        class="form-control"
        :value="fields[field.key] || ''"
        @input="updateField(field.key, $event.target.value)"
        :placeholder="field.placeholder || ''"
        :maxlength="field.maxLength || 200"
      />

      <span v-if="field.hint" class="form-hint">{{ field.hint }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import ImageUploader from '@/components/ImageUploader.vue';
import LineActionField from '@/components/LineActionField.vue';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';

const props = defineProps({
  presetKey: { type: String, required: true },
  schema: { type: Array, required: true },
  modelValue: { type: Object, default: () => ({}) }
});
const emit = defineEmits(['update:modelValue']);

const uploadFn = (file) => lineMsgTemplateService.uploadImage(file);
const fields = computed(() => props.modelValue || {});

/** 裁切輸出寬度（LINE Flex 圖片建議寬度上限） */
const CROP_BASE_WIDTH = 1024;

/**
 * 圖片欄位的裁切尺寸：
 * 比例可寫死在 field.cropRatio，或用 field.cropRatioFrom 指向同一份 schema 中的比例欄位；
 * field.cropWidth 可覆寫輸出寬度（例如頭像不需要 1024px）。
 * 沒指定比例時回傳 0，ImageUploader 就不會啟用裁切（維持舊行為）。
 */
function cropSize(field) {
  let ratio = field.cropRatio || '';
  if (!ratio && field.cropRatioFrom) {
    ratio = fields.value[field.cropRatioFrom]
      || props.schema.find(f => f.key === field.cropRatioFrom)?.default
      || '';
  }
  const [w, h] = String(ratio).split(':').map(Number);
  if (!w || !h) return { width: 0, height: 0, css: '' };
  const base = field.cropWidth || CROP_BASE_WIDTH;
  return {
    width: base,
    height: Math.round((base * h) / w),
    css: `${w} / ${h}`
  };
}

function updateField(key, val) {
  emit('update:modelValue', { ...fields.value, [key]: val });
}

// input[type=color] 只接受 #RRGGBB，其他值（含 8 碼透明度）退回白色
function swatchColor(val) {
  const v = (val || '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v : '#FFFFFF';
}

function isChecked(field) {
  const value = fields.value[field.key];
  return value === true || value === 'true' || value === 1 || value === '1';
}

function currentScheme(field) {
  return fields.value[field.key] || field.default || '';
}

function schemeLabel(field) {
  const key = currentScheme(field);
  return (field.options || []).find(o => o.value === key)?.label || '';
}

// buttons 類型工具函數
function btnActionValue(btn) {
  // 相容舊格式（只有 url 字串）
  if (btn.actionType) return btn;
  return { actionType: 'uri', uri: btn.url || '', text: '', data: '', displayText: '' };
}

function updateButtonField(fieldKey, idx, subKey, val) {
  const btns = [...(fields.value[fieldKey] || [{}])];
  btns[idx] = { ...btns[idx], [subKey]: val };
  emit('update:modelValue', { ...fields.value, [fieldKey]: btns });
}

function updateButtonAction(fieldKey, idx, actionObj) {
  const btns = [...(fields.value[fieldKey] || [{}])];
  btns[idx] = { ...btns[idx], ...actionObj };
  emit('update:modelValue', { ...fields.value, [fieldKey]: btns });
}

function addButtonRow(fieldKey) {
  const btns = [...(fields.value[fieldKey] || []), { label: '', actionType: 'uri', uri: '' }];
  emit('update:modelValue', { ...fields.value, [fieldKey]: btns });
}

function removeButtonRow(fieldKey, idx) {
  const btns = (fields.value[fieldKey] || []).filter((_, i) => i !== idx);
  emit('update:modelValue', { ...fields.value, [fieldKey]: btns.length ? btns : [{ label: '', actionType: 'uri', uri: '' }] });
}

// items 類型工具函數
function updateItemField(fieldKey, idx, subKey, val) {
  const items = [...(fields.value[fieldKey] || [{}])];
  items[idx] = { ...items[idx], [subKey]: val };
  emit('update:modelValue', { ...fields.value, [fieldKey]: items });
}

function addItemRow(fieldKey) {
  const items = [...(fields.value[fieldKey] || []), {}];
  emit('update:modelValue', { ...fields.value, [fieldKey]: items });
}

function removeItemRow(fieldKey, idx) {
  const items = (fields.value[fieldKey] || []).filter((_, i) => i !== idx);
  emit('update:modelValue', { ...fields.value, [fieldKey]: items.length ? items : [{}] });
}
</script>

<style scoped>
.preset-form { display: flex; flex-direction: column; gap: 14px; }
.form-group { display: flex; flex-direction: column; }
.form-label { font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 6px; }
.required-label::after { content: ' *'; color: #ef4444; }
.form-control {
  padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px;
  font-size: 14px; width: 100%; box-sizing: border-box;
}
.form-control:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.1); }
.form-control-sm { padding: 6px 10px; font-size: 13px; }

.form-hint { font-size: 12px; color: #9ca3af; margin-top: 4px; }

.checkbox-field {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; color: #374151; cursor: pointer;
}
.checkbox-field input { width: 16px; height: 16px; margin: 0; cursor: pointer; }

.color-field { display: flex; gap: 8px; align-items: center; }
.color-swatch {
  width: 40px; height: 36px; padding: 2px; flex-shrink: 0;
  border: 1px solid #d1d5db; border-radius: 6px; background: #fff; cursor: pointer;
}

.scheme-picker { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.scheme-swatch {
  width: 28px; height: 28px; border-radius: 50%; padding: 0;
  border: 2px solid #e5e7eb; background: #ccc; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 14px; line-height: 1; flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,.06);
}
.scheme-swatch.active {
  border-color: #111827;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #111827;
}
.scheme-swatch:hover:not(.active) { border-color: #9ca3af; }
.scheme-label { font-size: 13px; color: #6b7280; }

.items-editor { display: flex; flex-direction: column; gap: 6px; }
.item-row { display: flex; gap: 6px; align-items: center; }
.item-field { flex: 1; }

.buttons-editor { display: flex; flex-direction: column; gap: 10px; }
.button-row {
  border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px;
  display: flex; flex-direction: column; gap: 6px;
}
.button-row-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 2px;
}
.button-row-label { font-size: 12px; font-weight: 600; color: #6b7280; }

.icon-btn-sm {
  width: 24px; height: 24px; border: 1px solid #d1d5db; border-radius: 4px;
  background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #6b7280; font-size: 12px; flex-shrink: 0;
}
.icon-btn-sm.danger:hover { border-color: #ef4444; color: #ef4444; }
.btn-add-item {
  display: flex; align-items: center; gap: 4px; padding: 4px 10px;
  border: 1px dashed #d1d5db; border-radius: 6px; background: transparent;
  color: #6b7280; font-size: 12px; cursor: pointer; width: fit-content;
}
.btn-add-item:hover { border-color: #3b82f6; color: #3b82f6; }
</style>
